// Crypto Wallets → Configuration → Deposit & Withdrawal (SM-504,
// reshaped by SM-507).
//
// This page is where deposit and withdrawal availability and policy
// live. They used to be switches on Treasury → Currencies, which could
// say whether a currency was on but had nothing to say about the asset
// or the mode it was on FOR — so an operator with a closed route had no
// way to find out which of four layers had closed it.
//
// The hierarchy, least specific first:
//
//   PLATFORM        immutable capability ceilings (read-only here)
//   RAIL            the crypto rail's master feature flags
//   MODE            GAME | CASH
//   CURRENCY        RED | GREEN | GOLD
//   ASSET           the asset's own direction switch
//   ASSET_CURRENCY  one (asset, currency) mapping
//
// Two rules run over it and the page keeps them visibly apart:
// availability is DENY-WINS (every level must agree, and the row lists
// every level that says no), policy is MOST-SPECIFIC-WINS (each value
// names the level it came from). Getting those the wrong way round is
// the mistake this screen exists to prevent.
//
// SM-507 changed the shape rather than the rules. Deposits and
// withdrawals sit side by side at every level instead of behind a
// direction picker — one route, two independent answers — and the
// asset+currency table is the COMPLETE matrix, so a combination nobody
// has configured is a visible row rather than an absence. Rates are not
// edited here; each deposit cell links to its row on Asset Rates.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { errMsg, td, th, useCanConfig } from "@/components/wallet-ops/configShared";
import {
  BlockedBecause,
  DIRECTIONS,
  directionNoun,
  MODES,
  RAIL_CURRENCIES,
  RailStatePill,
  emptyRailPolicy,
  modeForCurrency,
  railKey,
} from "@/components/wallet-ops/railShared";
import {
  bulkTargets,
  buildMatrixRows,
  filterMatrixRows,
  isConfigured,
  matrixKey,
  type ConfiguredFilter,
  type EffectiveFilter,
  type MatrixRow,
} from "@/components/wallet-ops/railMatrix";
import {
  RailLevelEditor,
  type RailEditorScope,
} from "@/components/wallet-ops/RailLevelEditor";
import {
  deleteRailAssetCurrency,
  fetchCryptoAssets,
  fetchCryptoNetworks,
  fetchRailSnapshot,
  putRailAssetCurrency,
  putRailCurrency,
  putRailMode,
  railKeys,
  upsertCryptoAsset,
  walletKeys,
} from "@/lib/wallet-queries";
import type {
  CryptoAsset,
  RailDirection,
  RailLevelConfig,
  RailMapping,
  RailPutInput,
  RailSnapshot,
} from "@/lib/wallet-types";

/** Filter state, in the URL so a narrowed view is a shareable link. */
interface RailsSearch {
  q?: string;
  mode?: string;
  network?: string;
  asset?: string;
  currency?: string;
  configured?: ConfiguredFilter;
  effective?: EffectiveFilter;
}

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined =>
  typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;

export const Route = createFileRoute("/_app/wallet-ops/config/rails")({
  validateSearch: (search: Record<string, unknown>): RailsSearch => ({
    q: str(search.q),
    mode: oneOf(search.mode, MODES),
    network: str(search.network),
    asset: str(search.asset),
    currency: oneOf(search.currency, RAIL_CURRENCIES),
    configured: oneOf(search.configured, ["configured", "unconfigured"] as const),
    effective: oneOf(search.effective, ["open", "blocked", "locked"] as const),
  }),
  component: RailsConfigPage,
});

type Editing = { scope: RailEditorScope; configured?: RailLevelConfig } | null;

function RailsConfigPage() {
  const canConfig = useCanConfig();
  const qc = useQueryClient();
  const snapshot = useQuery({
    queryKey: railKeys.snapshot(),
    queryFn: fetchRailSnapshot,
  });
  // Full asset rows, because the ASSET-level switch is stored on the
  // asset itself: flipping it re-sends the whole definition, and the
  // snapshot's asset summary does not carry enough of one to do that
  // without wiping fields it never read.
  const assets = useQuery({
    queryKey: walletKeys.assets(),
    queryFn: () => fetchCryptoAssets(),
  });
  const networks = useQuery({
    queryKey: walletKeys.networksList(false),
    queryFn: () => fetchCryptoNetworks(),
  });
  const [editing, setEditing] = useState<Editing>(null);

  const save = useMutation({
    mutationFn: (v: { level: RailEditorScope["level"]; input: RailPutInput }) => {
      if (v.level === "MODE") return putRailMode(v.input);
      if (v.level === "CURRENCY") return putRailCurrency(v.input);
      return putRailAssetCurrency(v.input);
    },
    onSuccess: async () => {
      // Invalidate rather than patch: one save can change what several
      // rows resolve to, and only the server knows which.
      await qc.invalidateQueries({ queryKey: railKeys.all });
      setEditing(null);
    },
    meta: { successMessage: "Configuration saved." },
  });

  const remove = useMutation({
    mutationFn: (v: { assetID: string; currency: string; direction: RailDirection }) =>
      deleteRailAssetCurrency(v.assetID, v.currency, v.direction),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: railKeys.all });
    },
    meta: { successMessage: "Mapping removed." },
  });

  if (snapshot.isLoading) return <Skeleton className="h-96" />;
  if (snapshot.isError || !snapshot.data) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-no">
            The rail configuration could not be read, so nothing below can be
            trusted. {snapshot.error ? errMsg(snapshot.error) : ""}
          </p>
        </CardBody>
      </Card>
    );
  }

  const snap = snapshot.data;
  const flagsOff = DIRECTIONS.filter(
    (d) =>
      !snap.flags.enabled ||
      !(d === "DEPOSIT" ? snap.flags.deposit_enabled : snap.flags.withdraw_enabled),
  );

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="font-display text-2xl tracking-tight">
          Deposit &amp; withdrawal
        </h1>
        <p className="max-w-[85ch] text-sm text-muted">
          Availability and policy for every route, in three levels: mode,
          currency, and asset + currency. Every level must allow a route before
          it is open — a switch turned on here cannot re-open one a level above
          has closed. Policy works the other way: the most specific value set
          wins, and anything left alone is inherited.
        </p>
        <p className="max-w-[85ch] text-xs text-t-3">
          Deposits and withdrawals are separate everywhere on this page.
          Opening one never opens the other.
        </p>
        <div className="flex flex-wrap gap-4 text-xs">
          <Link to="/currencies" className="underline underline-offset-2 hover:text-t-1">
            Open Treasury → Currencies
          </Link>
          <Link
            to="/wallet-ops/config/asset-rates"
            className="underline underline-offset-2 hover:text-t-1"
          >
            Open Asset Rates
          </Link>
        </div>
      </header>

      {flagsOff.map((direction) => (
        <p key={direction} className="text-xs text-warn">
          {`The crypto rail's master switch is off for ${directionNoun(direction).toLowerCase()}, so every route below is closed in that direction whatever it is configured to be. `}
          <Link
            to="/wallet-ops/config/flags"
            className="underline underline-offset-2 hover:text-t-1"
          >
            Turn it on under Feature Flags
          </Link>
        </p>
      ))}

      <ModeSection snap={snap} canConfig={canConfig} onEdit={setEditing} />
      <CurrencySection snap={snap} canConfig={canConfig} onEdit={setEditing} />
      <AssetSection
        assets={assets.data ?? []}
        loading={assets.isLoading}
        canConfig={canConfig}
      />
      <MatrixSection
        snap={snap}
        networks={networks.data ?? []}
        canConfig={canConfig}
        onEdit={setEditing}
        onRemove={(assetID, currency, direction) =>
          remove.mutate({ assetID, currency, direction })
        }
        onBulk={async (inputs) => {
          // Sequential and fail-soft: one refused row must not abandon
          // the rest half-applied with nothing said about it. Whatever
          // failed is named back to the operator.
          const failed: string[] = [];
          for (const { input, label } of inputs) {
            try {
              await putRailAssetCurrency(input);
            } catch (e) {
              failed.push(`${label}: ${errMsg(e)}`);
            }
          }
          await qc.invalidateQueries({ queryKey: railKeys.all });
          if (failed.length) throw new Error(failed.join("; "));
        }}
      />

      {editing && (
        <RailLevelEditor
          scope={editing.scope}
          configured={editing.configured}
          inherited={
            snap.resolved[railKey(editing.scope.previewCurrency, editing.scope.direction)]
          }
          saving={save.isPending}
          error={save.error ? errMsg(save.error) : null}
          onSave={(input) => save.mutate({ level: editing.scope.level, input })}
          onCancel={() => {
            save.reset();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// --- shared cell parts ---

/** The two direction column headings, written out for the extractor. */
const DIRECTION_HEADINGS: { direction: RailDirection; label: string }[] = [
  { direction: "DEPOSIT", label: "Deposits" },
  { direction: "WITHDRAW", label: "Withdrawals" },
];

/** What THIS level stores, as distinct from what the route resolves to. */
function ConfiguredHere({
  enabled,
  inheritsFrom,
  locked,
}: {
  enabled: boolean | null | undefined;
  inheritsFrom: string;
  locked?: boolean;
}) {
  if (locked) return <span className="text-2xs text-t-3">not configurable</span>;
  if (enabled === null || enabled === undefined) {
    return <span className="text-2xs text-t-3">{`inherited from ${inheritsFrom}`}</span>;
  }
  return enabled ? (
    <Badge tone="success">Enabled here</Badge>
  ) : (
    <Badge tone="danger">Disabled here</Badge>
  );
}

// --- mode level ---

function ModeSection({
  snap,
  canConfig,
  onEdit,
}: {
  snap: RailSnapshot;
  canConfig: boolean;
  onEdit: (e: Editing) => void;
}) {
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Mode</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="max-w-[85ch] text-xs text-t-3">
          The top of the operator-owned ladder, and the defaults every level
          below inherits. GAME covers RED and GREEN; CASH covers GOLD. There is
          nothing above a mode to inherit from, so its switch is always set
          rather than inherited.
        </p>
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className={th}>Mode</th>
              {DIRECTION_HEADINGS.map((d) => (
                <th key={d.direction} className={th}>
                  {d.label}
                </th>
              ))}
              <th className={th}>Currencies</th>
            </tr>
          </thead>
          <tbody>
            {MODES.map((mode) => {
              const currencies = RAIL_CURRENCIES.filter((c) => modeForCurrency(c) === mode);
              return (
                <tr key={mode} className="border-b border-border/60">
                  <td className={td}>
                    <span className="font-medium">{mode}</span>
                  </td>
                  {DIRECTION_HEADINGS.map((d) => {
                    const cfg = snap.modes.find(
                      (m) => m.mode === mode && m.direction === d.direction,
                    );
                    const nounLower = d.label.toLowerCase();
                    return (
                      <td key={d.direction} className={td}>
                        <div className="flex items-center gap-2">
                          {cfg?.enabled ? (
                            <Badge tone="success">Enabled</Badge>
                          ) : (
                            <Badge tone="danger">Disabled</Badge>
                          )}
                          {canConfig && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                onEdit({
                                  scope: {
                                    level: "MODE",
                                    direction: d.direction,
                                    mode,
                                    title: `${mode} ${nounLower}`,
                                    previewCurrency: currencies[0] ?? "GOLD",
                                    enabledRequired: true,
                                    // A mode spans currencies with
                                    // different ceilings, so the mode
                                    // switch itself is never
                                    // ceiling-locked; the currencies
                                    // under it still are.
                                    ceilingAllows: true,
                                  },
                                  configured: cfg,
                                })
                              }
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td className={td}>
                    <div className="flex flex-wrap gap-3">
                      {currencies.map((c) => (
                        <span key={c} className="flex items-center gap-1 text-xs">
                          {c}
                          {DIRECTION_HEADINGS.map((d) => (
                            <RailStatePill
                              key={d.direction}
                              resolved={snap.resolved[railKey(c, d.direction)]}
                            />
                          ))}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

// --- currency level ---

function CurrencySection({
  snap,
  canConfig,
  onEdit,
}: {
  snap: RailSnapshot;
  canConfig: boolean;
  onEdit: (e: Editing) => void;
}) {
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Currency</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="max-w-[85ch] text-xs text-t-3">
          Each currency inherits its mode, or overrides it. Some routes are
          locked forever rather than switched off: RED can be bought but never
          cashed out, and GREEN can only be earned, granted or converted into.
          Locked routes show as locked and carry no switch, because there has
          never been one.
        </p>
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className={th}>Currency</th>
              {DIRECTION_HEADINGS.map((d) => (
                <th key={d.direction} className={th}>
                  {d.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {RAIL_CURRENCIES.map((currency) => (
              <tr key={currency} className="border-b border-border/60">
                <td className={td}>
                  <span className="font-medium">{currency}</span>
                  <span className="ml-2 text-2xs text-t-3">
                    {modeForCurrency(currency)}
                  </span>
                </td>
                {DIRECTION_HEADINGS.map((d) => {
                  const cfg = snap.currencies.find(
                    (c) => c.currency === currency && c.direction === d.direction,
                  );
                  const resolved = snap.resolved[railKey(currency, d.direction)];
                  const ceilingAllows =
                    snap.ceilings[railKey(currency, d.direction)] !== false;
                  const nounLower = d.label.toLowerCase();
                  return (
                    <td key={d.direction} className={td}>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <RailStatePill resolved={resolved} />
                          {canConfig && ceilingAllows && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                onEdit({
                                  scope: {
                                    level: "CURRENCY",
                                    direction: d.direction,
                                    currency,
                                    title: `${currency} ${nounLower}`,
                                    previewCurrency: currency,
                                    ceilingAllows,
                                  },
                                  configured: cfg,
                                })
                              }
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                        <ConfiguredHere
                          enabled={cfg?.enabled}
                          inheritsFrom={modeForCurrency(currency)}
                          locked={!ceilingAllows}
                        />
                        <BlockedBecause resolved={resolved} />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
}

// --- asset level ---

type Capability = "deposit" | "withdrawal";

// The asset's own direction switch (SM-277), moved here from the Assets
// page by SM-507. It is a rung of this ladder and belongs beside the
// others: an operator whose mapping is enabled but whose asset is dark
// needs both switches in one place to see which of the two to fix.
function AssetSection({
  assets,
  loading,
  canConfig,
}: {
  assets: CryptoAsset[];
  loading: boolean;
  canConfig: boolean;
}) {
  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Asset</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 p-0">
        <p className="max-w-[85ch] px-5 pt-5 text-xs text-t-3">
          The token's own gate, shared by every currency it is mapped to.
          Darkening an asset here closes it in all of them at once, which is
          what makes it the right switch for taking a token out of service and
          the wrong one for closing a single route.
        </p>
        {loading && (
          <div className="space-y-2 p-5">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        )}
        {!loading && assets.length === 0 && (
          <p className="px-5 py-10 text-center text-sm text-muted">
            No assets yet — define one under Configuration → Assets first.
          </p>
        )}
        {assets.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface-2">
                <tr>
                  <th className={`${th} px-5`}>Asset</th>
                  {DIRECTION_HEADINGS.map((d) => (
                    <th key={d.direction} className={th}>
                      {d.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => (
                  <AssetRow key={a.asset_id} asset={a} canConfig={canConfig} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function AssetRow({ asset, canConfig }: { asset: CryptoAsset; canConfig: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<Capability | null>(null);
  const [failed, setFailed] = useState<{ cap: Capability; msg: string } | null>(null);
  const save = useMutation({
    mutationFn: (next: CryptoAsset) => upsertCryptoAsset(next),
    onSuccess: async () => {
      setFailed(null);
      await qc.invalidateQueries({ queryKey: walletKeys.all });
    },
    onSettled: () => setBusy(null),
    meta: { successMessage: "Asset switch updated." },
  });

  // Each toggle re-sends the full asset with only its own capability
  // changed, so flipping one never disturbs the other.
  const toggle = (cap: Capability, value: boolean) => {
    setBusy(cap);
    setFailed(null);
    save.mutate(
      cap === "deposit"
        ? { ...asset, deposit_enabled: value }
        : { ...asset, withdrawal_enabled: value },
      { onError: (e) => setFailed({ cap, msg: errMsg(e) }) },
    );
  };

  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className={`${td} px-5 font-medium text-fg`}>
        {asset.symbol}
        <span className="ml-2 text-2xs text-t-3">{asset.network_id}</span>
      </td>
      <td className={td}>
        <AssetSwitch
          label="Deposits"
          enabled={asset.deposit_enabled}
          canConfig={canConfig}
          pending={busy === "deposit" && save.isPending}
          error={failed?.cap === "deposit" ? failed.msg : undefined}
          onChange={(v) => toggle("deposit", v)}
        />
      </td>
      <td className={td}>
        <AssetSwitch
          label="Withdrawals"
          enabled={asset.withdrawal_enabled}
          canConfig={canConfig}
          pending={busy === "withdrawal" && save.isPending}
          error={failed?.cap === "withdrawal" ? failed.msg : undefined}
          onChange={(v) => toggle("withdrawal", v)}
        />
      </td>
    </tr>
  );
}

function AssetSwitch({
  label,
  enabled,
  canConfig,
  pending,
  error,
  onChange,
}: {
  label: string;
  enabled: boolean;
  canConfig: boolean;
  pending: boolean;
  error?: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <Badge tone={enabled ? "success" : "muted"}>
          {enabled ? "Enabled" : "Disabled"}
        </Badge>
        {canConfig && (
          <label className="flex items-center gap-1.5 text-2xs text-t-2">
            <input
              type="checkbox"
              checked={enabled}
              disabled={pending}
              onChange={(e) => onChange(e.target.checked)}
              aria-label={`${label} enabled`}
            />
            {pending ? "Saving…" : enabled ? "On" : "Off"}
          </label>
        )}
      </div>
      {error && <p className="text-2xs text-no">{error}</p>}
    </div>
  );
}

// --- asset + currency level: the matrix ---

const CONFIGURED_OPTIONS = [
  { value: "configured", label: "Configured here" },
  { value: "unconfigured", label: "Not configured" },
];

const EFFECTIVE_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "blocked", label: "Blocked" },
  { value: "locked", label: "Locked" },
];

type BulkAction = {
  direction: RailDirection;
  enabled: boolean | null;
  label: string;
};

const BULK_ACTIONS: BulkAction[] = [
  { direction: "DEPOSIT", enabled: true, label: "Enable deposits" },
  { direction: "DEPOSIT", enabled: false, label: "Disable deposits" },
  { direction: "WITHDRAW", enabled: true, label: "Enable withdrawals" },
  { direction: "WITHDRAW", enabled: false, label: "Disable withdrawals" },
];

function MatrixSection({
  snap,
  networks,
  canConfig,
  onEdit,
  onRemove,
  onBulk,
}: {
  snap: RailSnapshot;
  networks: { network_id: string; name: string }[];
  canConfig: boolean;
  onEdit: (e: Editing) => void;
  onRemove: (assetID: string, currency: string, direction: RailDirection) => void;
  onBulk: (inputs: { input: RailPutInput; label: string }[]) => Promise<void>;
}) {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<BulkAction | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const networkNames = useMemo(() => {
    const out: Record<string, string> = {};
    for (const n of networks) out[n.network_id] = n.name || n.network_id;
    return out;
  }, [networks]);

  const rows = useMemo(() => buildMatrixRows(snap, networkNames), [snap, networkNames]);
  const visible = useMemo(() => filterMatrixRows(rows, search), [rows, search]);

  const setSearch = (next: Partial<RailsSearch>) =>
    navigate({
      to: "/wallet-ops/config/rails",
      search: (s) => ({ ...s, ...next }),
      replace: true,
    });

  const toggleRow = (row: MatrixRow) => {
    const key = matrixKey(row.assetID, row.currency);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const allVisibleSelected =
    visible.length > 0 &&
    visible.every((r) => selected.has(matrixKey(r.assetID, r.currency)));

  const targets = pending ? bulkTargets(visible, selected, pending.direction) : [];

  const runBulk = async () => {
    if (!pending) return;
    setBulkBusy(true);
    setBulkError(null);
    try {
      await onBulk(
        targets.map((row) => {
          const m = pending.direction === "DEPOSIT" ? row.deposit : row.withdraw;
          return {
            label: `${row.symbol} → ${row.currency}`,
            input: {
              direction: pending.direction,
              currency: row.currency,
              asset_id: row.assetID,
              enabled: pending.enabled,
              // The PUT stores the whole rung, so the row's existing
              // policy has to ride along or a bulk switch would silently
              // clear every override underneath it. A bulk action
              // changes availability and only availability.
              policy: m?.configured?.policy ?? emptyRailPolicy(),
            },
          };
        }),
      );
      setPending(null);
      setSelected(new Set());
    } catch (e) {
      setBulkError(errMsg(e));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Asset + currency</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="max-w-[85ch] text-xs text-t-3">
          Every route an operator could configure, whether or not anyone has.
          The network and asset definitions are shared and referenced, never
          copied: the same token can be mapped to more than one platform
          currency, each with its own rate and policy, and editing one mapping
          never moves another that happens to share its asset or its chain.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px] flex-1">
            <Input
              label="Search"
              value={search.q ?? ""}
              onChange={(e) => setSearch({ q: e.target.value || undefined })}
              placeholder="symbol, asset id or network"
            />
          </div>
          <Select
            label="Mode"
            value={search.mode ?? ""}
            onChange={(e) => setSearch({ mode: e.target.value || undefined })}
            placeholder="All modes"
            options={MODES.map((m) => ({ value: m, label: m }))}
          />
          <Select
            label="Network"
            value={search.network ?? ""}
            onChange={(e) => setSearch({ network: e.target.value || undefined })}
            placeholder="All networks"
            options={networks.map((n) => ({
              value: n.network_id,
              label: n.name || n.network_id,
            }))}
          />
          <Select
            label="Asset"
            value={search.asset ?? ""}
            onChange={(e) => setSearch({ asset: e.target.value || undefined })}
            placeholder="All assets"
            options={snap.assets.map((a) => ({
              value: a.asset_id,
              label: `${a.symbol} · ${a.network_id}`,
            }))}
          />
          <Select
            label="Currency"
            value={search.currency ?? ""}
            onChange={(e) => setSearch({ currency: e.target.value || undefined })}
            placeholder="All currencies"
            options={RAIL_CURRENCIES.map((c) => ({ value: c, label: c }))}
          />
          <Select
            label="Configured"
            value={search.configured ?? ""}
            onChange={(e) =>
              setSearch({ configured: (e.target.value || undefined) as ConfiguredFilter })
            }
            placeholder="Any"
            options={CONFIGURED_OPTIONS}
          />
          <Select
            label="Effective"
            value={search.effective ?? ""}
            onChange={(e) =>
              setSearch({ effective: (e.target.value || undefined) as EffectiveFilter })
            }
            placeholder="Any"
            options={EFFECTIVE_OPTIONS}
          />
          <p className="self-end pb-3 text-2xs text-t-3">
            {`${visible.length} of ${rows.length} routes`}
          </p>
        </div>

        {canConfig && selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-2/50 p-3">
            <span className="text-xs text-t-2">{`${selected.size} selected`}</span>
            {BULK_ACTIONS.map((a) => (
              <Button
                key={a.label}
                size="sm"
                variant="secondary"
                onClick={() => {
                  setBulkError(null);
                  setPending(a);
                }}
              >
                {a.label}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </div>
        )}

        {rows.length === 0 ? (
          <p className="text-sm text-muted">
            No assets are defined yet, so there are no routes to configure.
          </p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted">No route matches these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {canConfig && (
                    <th className={th}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        aria-label="Select every route shown"
                        onChange={(e) => {
                          // Scoped to what is on screen in both
                          // directions: unchecking must not silently
                          // drop rows selected under another filter.
                          const keys = visible.map((r) =>
                            matrixKey(r.assetID, r.currency),
                          );
                          setSelected((prev) => {
                            const next = new Set(prev);
                            for (const k of keys) {
                              if (e.target.checked) next.add(k);
                              else next.delete(k);
                            }
                            return next;
                          });
                        }}
                      />
                    </th>
                  )}
                  <th className={th}>Asset</th>
                  <th className={th}>Network</th>
                  <th className={th}>Currency</th>
                  {DIRECTION_HEADINGS.map((d) => (
                    <th key={d.direction} className={th}>
                      {d.label}
                    </th>
                  ))}
                  <th className={th}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <MatrixRowView
                    key={matrixKey(row.assetID, row.currency)}
                    row={row}
                    snap={snap}
                    canConfig={canConfig}
                    selected={selected.has(matrixKey(row.assetID, row.currency))}
                    onSelect={() => toggleRow(row)}
                    onEdit={onEdit}
                    onRemove={onRemove}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>

      <ConfirmModal
        open={pending !== null}
        title={pending?.label ?? ""}
        description={
          pending && (
            <div className="space-y-2 text-xs">
              <p>
                {targets.length === 0
                  ? "None of the selected routes can be changed in this direction — the rest are locked by a capability ceiling."
                  : `${targets.length} of ${selected.size} selected routes will be changed. The other selected routes are locked in this direction and are left alone.`}
              </p>
              {targets.length > 0 && (
                <ul className="max-h-48 space-y-0.5 overflow-y-auto">
                  {targets.map((row) => (
                    <li key={matrixKey(row.assetID, row.currency)}>
                      {`${row.symbol} → ${row.currency} (${row.networkName})`}
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-t-3">
                Only the switch changes. Any policy already set on these routes
                stays exactly as it is, and the other direction is untouched.
              </p>
            </div>
          )
        }
        confirmLabel={pending?.label}
        confirmDisabled={targets.length === 0}
        loading={bulkBusy}
        error={bulkError}
        tone={pending?.enabled === false ? "danger" : "primary"}
        onConfirm={runBulk}
        onCancel={() => {
          setPending(null);
          setBulkError(null);
        }}
      />
    </Card>
  );
}

function MatrixRowView({
  row,
  snap,
  canConfig,
  selected,
  onSelect,
  onEdit,
  onRemove,
}: {
  row: MatrixRow;
  snap: RailSnapshot;
  canConfig: boolean;
  selected: boolean;
  onSelect: () => void;
  onEdit: (e: Editing) => void;
  onRemove: (assetID: string, currency: string, direction: RailDirection) => void;
}) {
  return (
    <tr className="border-b border-border/60 align-top">
      {canConfig && (
        <td className={td}>
          <input
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            aria-label={`Select ${row.symbol} ${row.currency}`}
          />
        </td>
      )}
      <td className={td}>
        <span className="font-medium">{row.symbol}</span>
      </td>
      <td className={td}>
        <span className="text-2xs text-t-3">{row.networkName}</span>
      </td>
      <td className={td}>
        {row.currency}
        <span className="ml-2 text-2xs text-t-3">{row.mode}</span>
      </td>
      {DIRECTION_HEADINGS.map((d) => (
        <td key={d.direction} className={td}>
          <DirectionCell
            row={row}
            direction={d.direction}
            label={d.label}
            snap={snap}
            canConfig={canConfig}
            onEdit={onEdit}
            onRemove={onRemove}
          />
        </td>
      ))}
      <td className={td}>
        <RateCell row={row} />
      </td>
    </tr>
  );
}

function DirectionCell({
  row,
  direction,
  label,
  snap,
  canConfig,
  onEdit,
  onRemove,
}: {
  row: MatrixRow;
  direction: RailDirection;
  label: string;
  snap: RailSnapshot;
  canConfig: boolean;
  onEdit: (e: Editing) => void;
  onRemove: (assetID: string, currency: string, direction: RailDirection) => void;
}) {
  const m: RailMapping | undefined = direction === "DEPOSIT" ? row.deposit : row.withdraw;
  const ceilingAllows = snap.ceilings[railKey(row.currency, direction)] !== false;
  if (!m || !ceilingAllows) {
    return (
      <div className="space-y-1">
        <Badge tone="muted">Locked</Badge>
        <p className="text-2xs text-t-3">
          permanently unavailable — a capability ceiling, not a setting
        </p>
      </div>
    );
  }
  const nounLower = label.toLowerCase();
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <RailStatePill resolved={m.resolved} />
        {canConfig && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              onEdit({
                scope: {
                  level: "ASSET_CURRENCY",
                  direction,
                  currency: row.currency,
                  assetID: row.assetID,
                  title: `${row.symbol} → ${row.currency} ${nounLower}`,
                  previewCurrency: row.currency,
                  previewAssetID: row.assetID,
                  ceilingAllows,
                },
                configured: m.configured ?? {
                  level: "ASSET_CURRENCY",
                  direction,
                  currency: row.currency,
                  asset_id: row.assetID,
                  enabled: null,
                  policy: emptyRailPolicy(),
                },
              })
            }
          >
            Edit
          </Button>
        )}
        {canConfig && isConfigured(m) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onRemove(row.assetID, row.currency, direction)}
          >
            Remove
          </Button>
        )}
      </div>
      {/* Two facts, never merged: what this level stores, and what
          closed the route. BlockedBecause already names EVERY refusing
          level, so a third line repeating one of them would only make
          the operator read three sentences to learn two things. */}
      <ConfiguredHere enabled={m.configured?.enabled} inheritsFrom={row.currency} />
      <BlockedBecause resolved={m.resolved} />
    </div>
  );
}

// Rates are not editable here — the deposit rate is a per-pairing fact
// with its own page, and duplicating the editor would be a second place
// for the same number to be wrong. The cell states the fact and links to
// the exact row that owns it.
function RateCell({ row }: { row: MatrixRow }) {
  if (!row.deposit) return <span className="text-2xs text-t-3">—</span>;
  const rate = row.deposit.rate;
  return (
    <div className="space-y-1">
      {rate ? (
        <span className="text-xs">
          {rate.rate_num}:{rate.rate_den}
        </span>
      ) : (
        <span className="text-2xs text-warn">no rate — cannot credit</span>
      )}
      <Link
        to="/wallet-ops/config/asset-rates"
        search={{ open: `${row.assetID}|${row.currency}` }}
        className="block text-2xs underline underline-offset-2 hover:text-t-1"
      >
        {rate ? "Open on Asset Rates" : "Set one on Asset Rates"}
      </Link>
    </div>
  );
}
